import * as Sentry from "@sentry/react";
import { doc, getDoc, getFirestore } from "firebase/firestore";

const injectedGoogleFontHrefs = new Set();
const injectedLocalFontNames = new Set();

/** Force font-display: block sur les CSS Google Fonts (équivalent du @font-face local). */
export function withGoogleFontsDisplayBlock(fontUrl) {
  if (!fontUrl || !String(fontUrl).includes("fonts.googleapis.com")) {
    return fontUrl;
  }
  try {
    const url = new URL(fontUrl);
    url.searchParams.set("display", "block");
    return url.toString();
  } catch {
    if (/[?&]display=/i.test(fontUrl)) {
      return fontUrl.replace(/([?&]display=)[^&]*/i, "$1block");
    }
    return fontUrl.includes("?") ? `${fontUrl}&display=block` : `${fontUrl}?display=block`;
  }
}

async function loadMenuFont(fontUrl, fontName) {
  if (!fontUrl || !fontName) {
    return null;
  }

  try {
    if (fontUrl.includes("fonts.googleapis.com")) {
      const href = withGoogleFontsDisplayBlock(fontUrl);
      if (!injectedGoogleFontHrefs.has(href)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
        injectedGoogleFontHrefs.add(href);
      }
      // Comme l'ancien Player.js : appliquer le nom dès l'injection du stylesheet.
      return fontName;
    }

    if (!injectedLocalFontNames.has(fontName)) {
      const style = document.createElement("style");
      style.textContent = `
        @font-face {
          font-family: '${fontName}';
          src: url('${fontUrl}') format('truetype');
          font-display: block;
        }
      `;
      document.head.appendChild(style);
      injectedLocalFontNames.add(fontName);

      try {
        const font = new FontFace(fontName, `url(${fontUrl})`);
        await font.load();
        document.fonts.add(font);
      } catch (error) {
        try {
          const font = new FontFace(fontName, `url(${fontUrl})`);
          document.fonts.add(font);
        } catch (innerError) {
          Sentry.captureMessage(`Menu font loading failed: ${fontName} - ${innerError.message}`);
          return null;
        }
      }
    }

    return fontName;
  } catch (error) {
    Sentry.captureMessage(`Menu font loading failed: ${fontName} - ${error.message}`);
    return null;
  }
}

/** Même comportement que l'ancien Menu.js : fontFamily = nom seul, Roboto seulement si fallback explicite. */
export function getMenuFontStyle(loadedFont, useFallback) {
  if (loadedFont) {
    return { fontFamily: loadedFont };
  }
  if (useFallback) {
    return { fontFamily: "Roboto, sans-serif" };
  }
  return {};
}

/**
 * Police des descriptions / notes de fin / notes générales : descFont si défini, sinon textFontStyle (comportement historique).
 */
export function getMenuDescFontStyle({
  descFont,
  descFontNameFromMenu,
  descFontFallback,
  textFontStyle,
}) {
  if (descFont || descFontNameFromMenu || descFontFallback) {
    return getMenuFontStyle(descFont || descFontNameFromMenu, descFontFallback);
  }
  return textFontStyle || {};
}

async function applyFontFromMenuData(fontData, setFont, setFallback) {
  if (fontData?.name && fontData.location) {
    const loaded = await loadMenuFont(fontData.location, fontData.name);
    if (loaded) {
      setFont(loaded);
    } else {
      setFont(null);
      setFallback(true);
    }
  } else if (fontData?.name) {
    setFont(null);
    setFallback(true);
  } else {
    setFont(null);
  }
}

export async function loadMenuFonts(menuId, callbacks) {
  const {
    setMenuTitleFont,
    setMenuTextFont,
    setMenuDescFont,
    setMenuTitleFontFallback,
    setMenuTextFontFallback,
    setMenuDescFontFallback,
    setMenuFontsLoading,
  } = callbacks;

  if (!menuId || menuId === "0") {
    setMenuTitleFont(null);
    setMenuTextFont(null);
    if (setMenuDescFont) setMenuDescFont(null);
    setMenuTitleFontFallback(false);
    setMenuTextFontFallback(false);
    if (setMenuDescFontFallback) setMenuDescFontFallback(false);
    setMenuFontsLoading(false);
    return;
  }

  setMenuFontsLoading(true);
  setMenuTitleFontFallback(false);
  setMenuTextFontFallback(false);
  if (setMenuDescFontFallback) setMenuDescFontFallback(false);

  const db = getFirestore();
  const menuRef = doc(db, "menus", menuId);
  let menuSnap;
  try {
    menuSnap = await getDoc(menuRef);
  } catch (error) {
    setMenuFontsLoading(false);
    return;
  }

  const menuData = menuSnap.data();

  if (!menuData) {
    setMenuTitleFont(null);
    setMenuTextFont(null);
    if (setMenuDescFont) setMenuDescFont(null);
    setMenuFontsLoading(false);
    return;
  }

  await applyFontFromMenuData(menuData.titleFont, setMenuTitleFont, setMenuTitleFontFallback);
  await applyFontFromMenuData(menuData.textFont, setMenuTextFont, setMenuTextFontFallback);
  if (setMenuDescFont && setMenuDescFontFallback) {
    if (menuData.descFont?.name || menuData.descFont?.location) {
      await applyFontFromMenuData(menuData.descFont, setMenuDescFont, setMenuDescFontFallback);
    } else {
      setMenuDescFont(null);
      setMenuDescFontFallback(false);
    }
  }

  setMenuFontsLoading(false);
}
